import { supabase } from "@/integrations/supabase/client";
import type {
	CandidateScorecardRecord,
	ScorecardCategory,
	ScorecardCategoryInput,
	ScorecardData,
	ScorecardField,
	ScorecardFieldInput,
} from "@/types/scorecard";

// The scorecard tables are not present in the generated Database types
// (types.ts is stale repo-wide), so results are cast at the boundary —
// same approach as customFieldsApi.ts.

const CANDIDATE_SCORECARD_COLUMNS =
	"id, scorecard_data, scorecard_completed_by, scorecard_completed_at, scorecard_updated_at";

export async function fetchScorecardCategories(organizationId: string): Promise<ScorecardCategory[]> {
	const { data, error } = await supabase
		.from("scorecard_categories")
		.select("*")
		.eq("organization_id", organizationId)
		.order("display_order", { ascending: true });
	if (error) throw error;
	return (data ?? []) as ScorecardCategory[];
}

export async function upsertScorecardCategory(
	organizationId: string,
	category: ScorecardCategoryInput,
): Promise<ScorecardCategory> {
	const payload = { ...category, organization_id: organizationId };
	const query = category.id
		? supabase.from("scorecard_categories").update(payload).eq("id", category.id)
		: supabase.from("scorecard_categories").insert([payload]);
	const { data, error } = await query.select().single();
	if (error) throw error;
	return data as ScorecardCategory;
}

export async function deleteScorecardCategory(categoryId: string): Promise<void> {
	const { error } = await supabase
		.from("scorecard_categories")
		.delete()
		.eq("id", categoryId);
	if (error) throw error;
}

export async function fetchScorecardFields(organizationId: string): Promise<ScorecardField[]> {
	const { data, error } = await supabase
		.from("scorecard_fields")
		.select("*, category:scorecard_categories(*)")
		.eq("organization_id", organizationId)
		.order("display_order", { ascending: true });
	if (error) throw error;
	return (data ?? []) as ScorecardField[];
}

export async function upsertScorecardField(
	organizationId: string,
	field: ScorecardFieldInput,
): Promise<ScorecardField> {
	const payload = { ...field, organization_id: organizationId };
	const query = field.id
		? supabase.from("scorecard_fields").update(payload).eq("id", field.id)
		: supabase.from("scorecard_fields").insert([payload]);
	const { data, error } = await query.select().single();
	if (error) throw error;
	return data as ScorecardField;
}

export async function deleteScorecardField(fieldId: string): Promise<void> {
	const { error } = await supabase
		.from("scorecard_fields")
		.delete()
		.eq("id", fieldId);
	if (error) throw error;
}

/** Persists a new display_order for each given row (used by move up/down). */
export async function reorderScorecardRows(
	table: "scorecard_fields" | "scorecard_categories",
	updates: { id: string; display_order: number }[],
): Promise<void> {
	const results = await Promise.all(
		updates.map(({ id, display_order }) =>
			supabase.from(table).update({ display_order }).eq("id", id),
		),
	);
	const failed = results.find((result) => result.error);
	if (failed?.error) throw failed.error;
}

export async function fetchCandidateScorecard(
	candidateId: string,
	organizationId: string,
): Promise<CandidateScorecardRecord | null> {
	const { data, error } = await supabase
		.from("candidates")
		.select(CANDIDATE_SCORECARD_COLUMNS)
		.eq("id", candidateId)
		// candidates uses organization_uuid (existing column name), unlike the scorecard tables
		.eq("organization_uuid", organizationId)
		.maybeSingle();
	if (error) throw error;
	if (!data) return null;
	const record = data as unknown as CandidateScorecardRecord;
	return { ...record, scorecard_data: record.scorecard_data ?? {} };
}

/** Display name of the profile that last completed a scorecard, or null. */
export async function fetchEvaluatorName(userId: string): Promise<string | null> {
	const { data, error } = await supabase
		.from("profiles")
		.select("first_name, last_name")
		.eq("id", userId)
		.maybeSingle();
	if (error) throw error;
	const name = [data?.first_name, data?.last_name].filter(Boolean).join(" ").trim();
	return name || null;
}

export async function saveCandidateScorecard(
	candidateId: string,
	organizationId: string,
	scorecardData: ScorecardData,
	completedBy: string,
): Promise<CandidateScorecardRecord> {
	// scorecard_updated_at is maintained by a DB trigger on the candidates table
	const { data, error } = await supabase
		.from("candidates")
		.update({
			scorecard_data: scorecardData,
			scorecard_completed_by: completedBy,
			scorecard_completed_at: new Date().toISOString(),
		})
		.eq("id", candidateId)
		.eq("organization_uuid", organizationId)
		.select(CANDIDATE_SCORECARD_COLUMNS)
		.single();
	if (error) throw error;
	const record = data as unknown as CandidateScorecardRecord;
	return { ...record, scorecard_data: record.scorecard_data ?? {} };
}
